/**
 * QueryStatementBar — the PQL statement surface on the item list and the
 * Report Designer (LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §3.1, §6.1).
 *
 * Builder mode: a slim entry affordance ("Query statement" / "Ask AI").
 * Statement mode: a mono input with live parse — a valid statement applies on
 * every keystroke; an invalid one keeps the last good result and shows the
 * error (with position caret) inline, with "Fix with AI" alongside it. Below
 * the input, the deterministic plain-English strip renders what the applied
 * query means.
 *
 * AI is a coach beside the box, never a hidden filter: "Ask AI" and
 * "Fix with AI" both write their result into the same editable text input
 * every hand-typed statement uses — nothing is ever applied without passing
 * through the visible, re-parsed statement.
 *
 * The AI controls are drawn only when a model will answer (aiAvailability:
 * a key in this browser, or the deployment's own). When none will, they are
 * not drawn and one line says how to add a key — never a button that fails
 * after the click (build contract §2.8).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2, Sparkles, SquareFunction, X } from 'lucide-react';
import type { StatementQueryState } from '../hooks/useStatementQuery';
import { applyCompletion, completeAt, type Completion } from '../../../../lib/pql';
import { useAiControl } from '../../../../lib/ai/aiAvailability';
import { AskAiNeedsKey } from '../../../shared/AskAiNeedsKey';
import { QueryResultActions, type QueryViewState } from './QueryResultActions';

interface QueryStatementBarProps {
  statement: StatementQueryState;
  filteredCount: number;
  totalCount: number;
  /** Ids of the rows the applied query matches — what the "Use results" verbs act on. */
  matchedItemIds: string[];
  /** The list's presentation state, persisted alongside a saved query. */
  view: QueryViewState;
  /** The saved query these results were opened from, if any — the origin a derived rule records. */
  appliedReport?: { id: string; queryText: string } | null;
  /** Draw "Save query…" in Use results. Default true; the Report Designer has its own Save. */
  saveVerb?: boolean;
  /**
   * Draw the "Use results" menu (save, collection, pool, feed). Default true.
   * The item pool leaves it out: there the query only narrows the view of the
   * pool's own members (owner ruling, 2026-10-14) and makes nothing of them.
   */
  resultActions?: boolean;
  /** Open the plain-English box, focused, as soon as a model is known to answer (an empty Report Designer). */
  openAskAiFirst?: boolean;
}

const MAX_COMPLETIONS = 10;

const COMPLETION_KIND_LABEL: Record<Completion['kind'], string> = {
  field: 'field',
  operator: 'op',
  keyword: 'kw',
  value: 'value',
  function: 'fn',
};

export function QueryStatementBar({ statement, filteredCount, totalCount, matchedItemIds, view, appliedReport, saveVerb = true, resultActions = true, openAskAiFirst = false }: QueryStatementBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const aiInputRef = useRef<HTMLInputElement>(null);
  const [aiPromptOpen, setAiPromptOpen] = useState(false);
  const [aiDraft, setAiDraft] = useState('');
  const {
    mode,
    text,
    setText,
    error,
    english,
    canExitToBuilder,
    enterStatementMode,
    exitToBuilder,
    clear,
    aiEnabled,
    aiStatus,
    aiMessage,
    askAi,
    fixWithAi,
    catalog,
    appliedQuery,
    canonicalText,
  } = statement;

  // Decide before drawing: the AI controls appear only once a model is known
  // to answer; while the check runs nothing AI is drawn; when none is known to
  // answer (or the check could not be read) the one no-model line is drawn.
  const { drawable: aiDrawable, needsKey: aiNeedsKey } = useAiControl(aiEnabled);

  // ── autocomplete (PRD §3.1: fields, operators valid for the type, values) ──
  const [caret, setCaret] = useState(0);
  const [acOpen, setAcOpen] = useState(false);
  const [acIndex, setAcIndex] = useState(0);
  const completion = useMemo(
    () => (acOpen ? completeAt(text, caret, catalog) : null),
    [acOpen, text, caret, catalog],
  );
  const acItems = useMemo(() => (completion?.items ?? []).slice(0, MAX_COMPLETIONS), [completion]);
  useEffect(() => setAcIndex(0), [acItems]);

  const syncCaret = () => setCaret(inputRef.current?.selectionStart ?? text.length);

  const accept = (item: Completion) => {
    if (!completion) return;
    const next = applyCompletion(text, completion, item);
    setText(next.text);
    setCaret(next.cursor);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(next.cursor, next.cursor);
    });
  };

  const onStatementKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (acOpen && acItems.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAcIndex((i) => (i + 1) % acItems.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAcIndex((i) => (i - 1 + acItems.length) % acItems.length); return; }
      if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); accept(acItems[acIndex]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setAcOpen(false); return; }
    }
    if (e.key === 'Escape') inputRef.current?.blur();
    if (e.key === ' ' && e.ctrlKey) { e.preventDefault(); syncCaret(); setAcOpen(true); }
  };

  const useResults = resultActions && (
    <QueryResultActions
      query={appliedQuery}
      canonicalText={canonicalText}
      matchedItemIds={matchedItemIds}
      view={view}
      appliedReport={appliedReport}
      includeSave={saveVerb}
    />
  );

  const submitAiPrompt = () => {
    if (!aiDraft.trim()) return;
    void askAi(aiDraft.trim());
    setAiPromptOpen(false);
  };

  const openAiPrompt = () => {
    setAiDraft('');
    setAiPromptOpen(true);
    requestAnimationFrame(() => aiInputRef.current?.focus());
  };

  const openedFirstRef = useRef(false);
  useEffect(() => {
    if (!openAskAiFirst || !aiDrawable || openedFirstRef.current) return;
    openedFirstRef.current = true;
    setAiDraft('');
    setAiPromptOpen(true);
    requestAnimationFrame(() => aiInputRef.current?.focus());
  }, [openAskAiFirst, aiDrawable]);

  if (mode === 'builder') {
    return (
      <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-4 py-1">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={enterStatementMode}
            className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition"
            title="Edit the current filters as a PQL statement"
          >
            <SquareFunction className="h-3.5 w-3.5" />
            Query statement
          </button>
          {aiDrawable && (
            <button
              type="button"
              onClick={openAiPrompt}
              className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition"
              title="Describe what you want in plain English"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Ask AI
            </button>
          )}
          <span className="ml-auto">{useResults}</span>
        </div>
        {aiPromptOpen && (
          <AiPromptRow
            ref={aiInputRef}
            value={aiDraft}
            onChange={setAiDraft}
            onSubmit={submitAiPrompt}
            onCancel={() => setAiPromptOpen(false)}
            busy={aiStatus === 'loading'}
          />
        )}
        {aiNeedsKey && <AskAiNeedsKey className="pb-1" />}
      </div>
    );
  }

  return (
    <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-4 py-2">
      <div className="flex items-center gap-2">
        <SquareFunction className="h-4 w-4 shrink-0 text-[var(--text-ter)]" />
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
              setAcOpen(true);
            }}
            onKeyDown={onStatementKeyDown}
            onSelect={syncCaret}
            onClick={() => { syncCaret(); setAcOpen(true); }}
            onFocus={() => { syncCaret(); setAcOpen(true); }}
            onBlur={() => setAcOpen(false)}
            role="combobox"
            aria-expanded={acOpen && acItems.length > 0}
            aria-controls="pql-completions"
            aria-autocomplete="list"
            placeholder='e.g. category IN (problem, risk) AND status = active ORDER BY updated DESC'
            spellCheck={false}
            autoComplete="off"
            className={`w-full font-mono text-[13px]  border px-3 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 ${
              error
                ? 'border-[var(--coral)] focus:border-[var(--coral)] focus:ring-red-100 dark:border-[var(--coral)] dark:focus:ring-red-900/40'
                : 'border-[var(--rule)] focus:border-[var(--slate)] focus:ring-indigo-100 dark:border-[var(--rule)] dark:focus:ring-indigo-900/40'
            }`}
          />
          {text && (
            <button
              type="button"
              onClick={clear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
              title="Clear statement"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {acOpen && acItems.length > 0 && (
            <ul
              id="pql-completions"
              role="listbox"
              className="absolute left-0 top-full z-30 mt-1 w-full max-w-md border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] shadow-lg py-1 text-[12px]"
            >
              {acItems.map((item, i) => (
                <li
                  key={`${item.kind}:${item.insert}`}
                  role="option"
                  aria-selected={i === acIndex}
                  onMouseDown={(e) => { e.preventDefault(); accept(item); }}
                  onMouseEnter={() => setAcIndex(i)}
                  className={`flex items-center gap-2 px-3 py-1 cursor-pointer ${
                    i === acIndex ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : 'text-[var(--text-sec)]'
                  }`}
                >
                  <span className="font-mono">{item.label}</span>
                  {item.detail && <span className="text-[var(--text-ter)] truncate">{item.detail}</span>}
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-[var(--text-ter)]">
                    {COMPLETION_KIND_LABEL[item.kind]}
                  </span>
                </li>
              ))}
              <li className="px-3 pt-1 text-[10px] text-[var(--text-ter)] border-t border-[var(--rule)] mt-1" aria-hidden>
                ↑↓ choose · Tab / Enter accept · Ctrl+Space reopen
              </li>
            </ul>
          )}
        </div>
        {aiDrawable && (
          <button
            type="button"
            onClick={() => (aiPromptOpen ? setAiPromptOpen(false) : openAiPrompt())}
            className="shrink-0 flex items-center gap-1 text-[11px] font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition"
            title="Describe what you want in plain English"
          >
            {aiStatus === 'loading' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Ask AI
          </button>
        )}
        <span className="shrink-0 text-[11px] tabular-nums text-[var(--text-ter)] dark:text-[var(--text-sec)]">
          {filteredCount} / {totalCount}
        </span>
        {useResults}
        <button
          type="button"
          onClick={() => exitToBuilder()}
          disabled={!canExitToBuilder}
          className="shrink-0 text-[11px] font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] disabled:opacity-40 disabled:cursor-not-allowed transition"
          title={
            canExitToBuilder
              ? 'Show this query in the filter builder'
              : 'This query uses NOT or nesting the builder can’t show — it stays in statement mode'
          }
        >
          Builder
        </button>
      </div>

      {aiPromptOpen && (
        <AiPromptRow
          ref={aiInputRef}
          value={aiDraft}
          onChange={setAiDraft}
          onSubmit={submitAiPrompt}
          onCancel={() => setAiPromptOpen(false)}
          busy={aiStatus === 'loading'}
        />
      )}

      <div className="mt-1 pl-6 text-[11px] leading-4">
        {error ? (
          <span className="flex items-start gap-1 text-[var(--coral)] dark:text-[var(--coral)]">
            <AlertCircle className="mt-px h-3 w-3 shrink-0" />
            <span>
              {error.message}
              <span className="text-[var(--coral)] dark:text-[var(--coral)]"> (at position {error.start})</span>
            </span>
            {aiDrawable && (
              <button
                type="button"
                onClick={() => void fixWithAi()}
                disabled={aiStatus === 'loading'}
                className="ml-1 inline-flex items-center gap-1 font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] disabled:opacity-50 transition"
              >
                {aiStatus === 'loading' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                Fix with AI
              </button>
            )}
          </span>
        ) : (
          <span className="italic text-[var(--text-ter)] dark:text-[var(--text-ter)]">{english}</span>
        )}
        {!error && aiStatus === 'error' && aiMessage && (
          <span className="ml-2 text-[var(--mustard)] dark:text-[var(--mustard)]">{aiMessage}</span>
        )}
        {!error && aiStatus === 'idle' && aiMessage && (
          <span className="ml-2 text-[var(--text-ter)] dark:text-[var(--text-ter)]">✦ {aiMessage}</span>
        )}
      </div>
      {aiNeedsKey && <AskAiNeedsKey className="mt-1 pl-6" />}
    </div>
  );
}

interface AiPromptRowProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  busy: boolean;
}

function AiPromptRow({
  value,
  onChange,
  onSubmit,
  onCancel,
  busy,
  ref,
}: AiPromptRowProps & { ref: React.Ref<HTMLInputElement> }) {
  return (
    <div className="mt-1.5 flex items-center gap-2 border border-[var(--slate)] dark:border-[var(--slate)]/60 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 px-2 py-1.5">
      <Sparkles className="h-3.5 w-3.5 shrink-0 text-[var(--text-ter)]" />
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        placeholder="Describe what you want — e.g. “active risks nobody's touched in a month”"
        disabled={busy}
        autoComplete="off"
        className="flex-1 bg-transparent text-[13px] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)]/70 dark:placeholder:text-[var(--text-ter)]/50 focus:outline-none disabled:opacity-60"
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={busy || !value.trim()}
        className="shrink-0 text-[11px] font-semibold text-[var(--text-sec)] disabled:opacity-40 transition"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Go'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="shrink-0 text-[var(--text-sec)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
